import { useState } from 'react';
import moment from 'moment/moment';
import { message } from 'antd';
import { laboratoryService, sendOrderToBupa, sendOrderToBionet, sendOrderToSofsalud } from '../../../services/laboratory';
import { getAsset } from '../../../services/general';
import { getActiveProviders } from '../../../services/providers';

const UseStructuredResults = () => {
  const [loading, setLoading] = useState({
    results: false,
    documents: false,
    orderRequest: false,
  });
  const [showModal, setShowModal] = useState(false);

  const dateTranslation = (isoDate) => {
    const date = moment(isoDate).utcOffset(-180);
    const humanLanguageDate = date.locale('es-CL').format('dddd DD [de] MMMM [a las] HH:mm:ss [hrs]');
    const stringToReplace = humanLanguageDate.substring(0, 1);
    const formattedDateWithUppercaseDay = humanLanguageDate.replace(stringToReplace, stringToReplace.toLocaleUpperCase());
    return formattedDateWithUppercaseDay;
  };

  const dateTranslationShort = (isoDate) => {
    const date = moment(isoDate).utcOffset(-180);
    return date.locale('es-CL').format('DD [de] MMMM [a las] HH:mm [hrs]');
  };

  const getPdfResults = async (admissionPatientId) => {
    if (admissionPatientId) {
      setLoading((loading) => ({ ...loading, results: true }));
      try {
        const response = await laboratoryService.checkCompletedResults({
          admissionPatientId: admissionPatientId,
          providerCode: 'bupa',
        });

        if (response) {
          return response;
        }
      } catch (error) {
        console.error(`Error en al obtener Resultados PDF: ${error}`);
        handleMessageError(`Error en al obtener Resultados PDF: ${error}`);
      } finally {
        setLoading((loading) => ({ ...loading, results: false }));
      }
    }
  };

  const handleDownloadDocument = async (admissionPatientId) => {
    const response = await getPdfResults(admissionPatientId);

    if (response.asset && response.asset.id && response.asset.type) {
      setLoading((loading) => ({ ...loading, documents: true }));
      try {
        const url = await getAsset({
          field: 'document',
          id: response.asset.id,
          type: response.asset.type,
        });
        const link = document.createElement('a');
        link.href = url.document_url;
        link.download = 'laboratory-results.pdf';
        link.setAttribute('target', '_blank');
        link.click();
      } catch (error) {
        console.error(`Error en al Descargar PDF: ${error}`);
        handleMessageError(`Error en al Descargar PDF: ${error}`);
      } finally {
        setLoading((loading) => ({ ...loading, documents: false }));
      }
    }
  };

  const handleMessageError = (text) => {
    message.error({
      content: text,
      style: {
        marginTop: '45vh',
      },
    });
  };

  const handleRequestOrder = async (admissionPatientId, branchOfficeId) => {
    const providers = getActiveProviders();

    if (!providers?.[branchOfficeId]?.laboratory) {
      message.warning({
        content: 'No hay proveedores de laboratorio activos',
        style: { marginTop: '45vh', fontSize: 16 },
      });
      return;
    }

    setLoading((loading) => ({ ...loading, orderRequest: true }));

    try {
      const promises = [];

      if (providers[branchOfficeId].laboratory.includes('bupa')) {
        promises.push(sendOrderToBupa({ admissionPatientId }));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (providers[branchOfficeId].laboratory.includes('bionet')) {
        promises.push(sendOrderToBionet({ admissionPatientId }));
      } else {
        promises.push(Promise.resolve(null));
      }

      if (providers[branchOfficeId].laboratory.includes('sofsalud')) {
        promises.push(sendOrderToSofsalud({ admissionPatientId }));
      } else {
        promises.push(Promise.resolve(null));
      }

      const [bupaRes, bionetRes, sofsaludRes] = await Promise.all(promises);

      const isBupaOk = !bupaRes || [200, 208].includes(bupaRes.statusCode);
      const isBionetOk = !bionetRes || [200, 208].includes(bionetRes.statusCode);
      const isSofsaludOk = !sofsaludRes || [200, 208].includes(sofsaludRes.statusCode);

      if (!isBupaOk) {
        message.error({
          content: 'Error al enviar orden a Bupa',
          style: { marginTop: '45vh', fontSize: 16 },
        });
      }
      if (!isBionetOk) {
        message.error({
          content: 'Error al enviar orden a Bionet',
          style: { marginTop: '45vh', fontSize: 16 },
        });
      }
      if (!isSofsaludOk) {
        message.error({
          content: 'Error al enviar orden a Sofsalud',
          style: { marginTop: '45vh', fontSize: 16 },
        });
      }
      if (isBupaOk && isBionetOk && isSofsaludOk) {
        message.success({
          content: 'Orden solicitada correctamente',
          style: { marginTop: '45vh', fontSize: 16 },
        });
      }
    } catch (error) {
      message.error({
        content: 'Error al solicitar orden de laboratorio',
        style: { marginTop: '45vh', fontSize: 16 },
      });
      console.error(error);
    } finally {
      setLoading((loading) => ({ ...loading, orderRequest: false }));
    }
  };

  return {
    loading,
    showModal,
    setShowModal,
    handleDownloadDocument,
    handleRequestOrder,
    dateTranslation,
    dateTranslationShort,
  };
};

export default UseStructuredResults;